import { MdsIcon } from "@/design-system/components/icon";
import styled from "@emotion/styled";
import { Link } from "react-aria-components";
import { ChipColor, ChipVariant } from "./chip.types";
import { MDSIconButton } from "@/mds/components/buttons/icon-button";

export const StyledInner = styled.div<{ maxWidth?: number | string }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  max-width: ${({ maxWidth }) =>
    typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth || "200px"};
  min-width: 0;
  flex-shrink: 0;
`;

export const StyledText = styled.span<{ $variant: ChipVariant }>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  padding-right: ${({ theme }) => theme.spacing.xxs};
  color: ${({ theme }) => theme.colors.text.primary};

  ${({ $variant, theme }) => {
    if ($variant === ChipVariant.Source) {
      return `
        font-size: ${theme.fontSizes.xxsmall};
        line-height: ${theme.lineHeights.xsmall};
      `;
    }

    return `
      font-size: ${theme.fontSizes["text-xs"]};
      line-height: ${theme.lineHeights["text-xs"]};
    `;
  }}
`;

export const StyledIcon = styled(MdsIcon)<{ $variant: ChipVariant }>`
  color: ${({ theme }) => theme.colors.icon.primary};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .mds-icon {
    font-size: ${({ theme }) => theme.fontSizes.xxsmall};
    color: ${({ theme }) => theme.colors.icon.primary};
    width: 13px;
    height: 13px;
  }
`;

export const StyledChipButton = styled(Link)<{ $color: ChipColor; $variant: ChipVariant }>`
  display: inline-flex;
  white-space: nowrap;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  outline: none;
  border: none;
  cursor: pointer;
  transition: 0.25s ease;
  user-select: none;

  /* Size xs styles (same as MDSButton xs) */
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  font-size: ${({ theme }) => theme.fontSizes.xxsmall};
  padding: ${({ theme }) => `${theme.spacing["0.5"]} ${theme.spacing["1"]}`};
  line-height: ${({ theme }) => theme.lineHeights.xxsmall};

  /* Conditional styles based on color */
  ${({ $color, theme }) => {
    if ($color === "grey") {
      return `
        background-color: ${theme.colors.grey.x50};
        color: ${theme.colors.grey.x800};
        box-shadow: none;

        &[data-hovered]:not([data-disabled]) {
          background-color: ${theme.colors.grey.x100};
          transition: 0.2s all;
        }

        &[data-pressed]:not([data-disabled]) {
          background-color: ${theme.colors.grey.x200};
          transition: 0.2s all;
        }

        &[data-disabled] {
          opacity: 0.4;
          cursor: not-allowed;
          background-color: ${theme.colors.grey.x50};
        }
      `;
    } else {
      // Default kind - matches MDSButton highlight color
      return `
        background-color: ${theme.colors.background.button.chip.default};
        color: ${theme.colors.text.secondary};
        box-shadow: none;

        &[data-hovered]:not([data-disabled]) {
          background-color: ${theme.colors.background.button.chip.hover};
          transition: 0.2s all;
        }

        &[data-disabled] {
          opacity: 0.4;
          cursor: not-allowed;
          background-color: ${theme.colors.background.button.chip.default};
        }
      `;
    }
  }}
`;

export const StyledRemoveButton = styled(MDSIconButton)`
  padding: 0;

  &[data-hovered]:not([data-disabled]) {
    background-color: unset;
  }

  .mds-icon {
    color: ${({ theme }) => theme.colors.icon.placeholder};
  }
`;
